import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { PASSWORD_MIN_LENGTH } from '@repo/auth-contracts';
import { Alert } from '@/components/alert';
import { AuthCard } from '@/components/auth-card';
import { Button } from '@/components/button';
import { PageFooter } from '@/components/page-footer';
import { PasswordField } from '@/components/password-field';
import { env } from '@/lib/env';
import { InteractionApiError } from '@/lib/interaction-api';
import { useLocale } from '@/lib/locale-context';
import { copyFor, messageFor } from '@/lib/messages';
import { errorHome } from '@/lib/error-home';
import { createRecoveryApi } from '@/lib/recovery-api';
import { useRecoveryCsrf } from './use-recovery-csrf';
import { BackToSignIn } from './back-to-sign-in';

/**
 * Sets the new password from the emailed link. `complete` is honest: a stale,
 * spent, or invented token resolves to `invalid_token`, so this screen shows a
 * "request a new link" state rather than a success screen over a password it
 * never changed (docs/auth/40-features/password-reset.md AC6). A good token
 * resolves to the done screen.
 */
export function ResetPassword({ token }: { token: string }) {
  const { locale } = useLocale();
  const copy = useMemo(() => copyFor(locale), [locale]);
  const api = useMemo(() => createRecoveryApi(env.authApiUrl), []);
  const { csrfToken, bootstrapError, retryBootstrap } = useRecoveryCsrf(api);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<'form' | 'done' | 'expired'>('form');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const usable = password.length >= PASSWORD_MIN_LENGTH;

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!usable || !csrfToken) return;
      setPending(true);
      setErrorCode(null);
      try {
        const result = await api.complete(token, password, csrfToken);
        setOutcome(result.status === 'invalid_token' ? 'expired' : 'done');
      } catch (error) {
        setErrorCode(
          error instanceof InteractionApiError ? error.code : 'unknown',
        );
      } finally {
        setPending(false);
      }
    },
    [api, csrfToken, password, token, usable],
  );

  const footer = <PageFooter />;

  // An unusable password is the field's own error; everything else (throttle,
  // network, CSRF) is form-level. Same single-home split as the interaction
  // steps.
  const fieldError =
    errorCode && errorHome(errorCode) === 'password'
      ? messageFor(errorCode, locale)
      : null;
  const formError =
    errorCode && errorHome(errorCode) !== 'password'
      ? messageFor(errorCode, locale)
      : null;

  if (outcome === 'done') {
    return (
      <AuthCard title={copy.resetDoneTitle} footer={footer}>
        <p role="status" className="text-sm leading-relaxed text-buds-muted">
          {copy.resetDoneBody}
        </p>
        <BackToSignIn label={copy.backToSignIn} />
      </AuthCard>
    );
  }

  if (outcome === 'expired') {
    return (
      <AuthCard title={copy.resetExpiredTitle} footer={footer}>
        <p role="status" className="text-sm leading-relaxed text-buds-muted">
          {copy.resetExpiredBody}
        </p>
        <Button
          type="button"
          onClick={() => {
            window.location.href = '/forgot-password';
          }}
        >
          {copy.resetExpiredAction}
        </Button>
        <BackToSignIn label={copy.backToSignIn} />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={copy.resetTitle}
      subtitle={copy.resetSubtitle}
      footer={footer}
    >
      <form onSubmit={(e) => void submit(e)} noValidate className="space-y-5">
        {bootstrapError ? (
          <div className="space-y-3">
            <Alert tone="error">{messageFor(bootstrapError, locale)}</Alert>
            <Button type="button" variant="outline" onClick={retryBootstrap}>
              {copy.errorRetry}
            </Button>
          </div>
        ) : null}
        {formError ? <Alert tone="error">{formError}</Alert> : null}
        <PasswordField
          id="new-password"
          label={copy.passwordNewLabel}
          error={fieldError}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          showLabel={copy.passwordShow}
          hideLabel={copy.passwordHide}
          hint={copy.passwordHint}
          autoFocus
        />
        {/* Disabled until the CSRF token is in hand — see forgot-password. */}
        <Button
          type="submit"
          loading={pending}
          disabled={!usable || !csrfToken}
        >
          {copy.resetSubmit}
        </Button>
      </form>
      <BackToSignIn label={copy.backToSignIn} />
    </AuthCard>
  );
}
